// Everything the workbook of a v2 project holds, in order, built from the structure (pure):
// [ნაკვეთი] → per object: TOTAL, its discipline sheets ("object" order) — or all TOTALs first
// and the discipline sheets grouped by discipline ("discipline" order).

import {
  LOCATION_RULES,
  categories,
  codes,
  effectiveLocation,
  excelRoles,
  isLinked,
  isSingleBuilding,
  numberedObjects,
  sheetList,
  tabPrefix,
  totalSheetName,
  walkTree,
} from "../../structure/structureRules.js";
import { builtInFactor } from "../../structure/markups.js";
import { buildDisciplineSheet, buildPlotSheet, buildTotalSheet } from "./sheetModel.js";

export const PLOT_SHEET = "ნაკვეთი";
const SPATIAL = new Set(["vol", "floor", "ztype"]);

const locationText = (rule) => (LOCATION_RULES[rule] || "").replace(/^ობიექტი › ?/, "");

/** Systems (blocks) of a sheet node, in order; a "one sheet" discipline takes those of its sub-disciplines. */
function blocksOf(tree, node) {
  const out = [];
  const rowCodes = codes(tree);
  const walk = (list) =>
    list.forEach((n) => {
      if (n.on === false || n.hidden) return;
      if (n.sys) {
        const location = effectiveLocation(tree, n.id);
        out.push({
          id: n.id,
          code: rowCodes[n.id] || "",
          name: n.name,
          location,
          locationLabel: locationText(location),
          locShow: n.locShow || (SPATIAL.has(location) ? "head" : "col"),
        });
      } else walk(n.kids);
    });
  walk(node.kids);
  return out;
}

/**
 * contents: { "<objectId>|<nodeId>": rows } (source objects only; linked buildings reuse their source's).
 * info: { project, date, client, docNo, docTitle, preparedBy, location }
 * Returns { sheets: [{ tab, objectId, nodeId, role: "plot"|"total"|"discipline", model }] }.
 */
export function planWorkbook({ site, trees, settings, info, contents }) {
  const frozen = !!settings.frozen;
  const english = settings.english !== false;
  const markups = settings.markups || [];
  const factor = settings.inUnit ? builtInFactor(markups) : 1;
  const objects = numberedObjects(site);
  const perObject = [];

  objects.forEach((obj) => {
    const sourceId = isLinked(site, obj) ? obj.typ : obj.id;
    const tree = trees[sourceId] || [];
    const rowCodes = codes(tree, frozen);
    const roles = excelRoles(tree);
    const cats = categories(tree);
    const sheets = sheetList(tree, rowCodes, tabPrefix(site, obj.number)).filter((s) => !s.err);
    const sheetByNode = Object.fromEntries(sheets.map((s) => [s.id, s]));
    const topOf = {};
    tree.forEach((top) => walkTree([top], (n) => (topOf[n.id] = top)));

    const disciplineSheets = [];
    walkTree(tree, (n) => {
      const sheet = sheetByNode[n.id];
      if (!sheet || roles[n.id] !== "sheet") return;
      const top = topOf[n.id];
      const kind = cats[top.id] === "hard" ? "works" : "simple";
      const model = buildDisciplineSheet({
        objectId: sourceId === obj.id ? obj.id : `${obj.id}=${sourceId}`,
        nodeId: n.id,
        kind,
        band: [info.docTitle, info.project, `${obj.number} ${obj.name}`, `${rowCodes[top.id] || ""} ${top.name}`].filter(Boolean).join(" · "),
        code: rowCodes[n.id],
        title: n.name,
        titleEn: n.en,
        english,
        blocks: blocksOf(tree, n),
        rows: contents[`${sourceId}|${n.id}`] || [],
        markups,
        inUnit: !!settings.inUnit,
        priceFactor: factor,
        detail: settings.detail || "detailed",
      });
      disciplineSheets.push({ tab: sheet.tab, objectId: obj.id, sourceId, nodeId: n.id, role: "discipline", model, linked: sourceId !== obj.id });
    });

    const modelByNode = Object.fromEntries(disciplineSheets.map((s) => [s.nodeId, s]));
    const lines = [];
    walkTree(tree, (n, depth) => {
      const role = roles[n.id];
      if (role !== "sum" && role !== "sheet") return;
      if (role === "sheet" && !modelByNode[n.id]) return;
      const sheet = modelByNode[n.id];
      lines.push({
        id: n.id,
        depth,
        code: rowCodes[n.id] || "",
        name: english && n.en ? `${n.name} | ${n.en}` : n.name,
        role,
        category: cats[topOf[n.id].id],
        children: n.kids.map((k) => k.id),
        tab: sheet?.tab,
        sheetKind: sheet?.model.kind,
        sumRow: sheet?.model.sumRow,
        finalRow: sheet?.model.finalRow,
        col: sheet?.model.col,
      });
    });
    const totalTab = totalSheetName(site, obj.number);
    const total = buildTotalSheet({
      objectId: obj.id,
      title: { ...info, objectName: isSingleBuilding(site) ? "" : `${obj.number} · ${obj.name}` },
      lines,
      markups,
      english,
    });
    perObject.push({ obj, total: { tab: totalTab, objectId: obj.id, role: "total", model: total }, disciplineSheets });
  });

  const sheets = [];
  if (objects.length > 1) {
    const plot = buildPlotSheet({
      title: { ...info, plotName: site.name },
      objects: perObject.map((p) => ({ objectId: p.obj.id, number: p.obj.number, name: p.obj.name, total: p.total.tab, sumRow: p.total.model.sumRow })),
      markups,
    });
    sheets.push({ tab: PLOT_SHEET, objectId: "plot", role: "plot", model: plot });
  }
  if (settings.order === "discipline" && objects.length > 1) {
    perObject.forEach((p) => sheets.push(p.total));
    const byCode = perObject.flatMap((p) => p.disciplineSheets.map((s) => ({ s, code: s.tab.replace(/^\d+-/, ""), objNo: p.obj.number })));
    byCode.sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }) || a.objNo - b.objNo);
    byCode.forEach((x) => sheets.push(x.s));
  } else {
    perObject.forEach((p) => sheets.push(p.total, ...p.disciplineSheets));
  }
  return { sheets };
}

/** Sheets whose content is saved back (source objects only, not linked copies). */
export const savableSheets = (plan) => plan.sheets.filter((s) => s.role === "discipline" && !s.linked);
